import { useState } from 'react';
import { useStore } from '../store/useStore';
import { useT } from '../i18n/useT';
import { useDocControllerContext } from '../hooks/DocControllerContext';
import { importFromUrl } from '../lib/import';
import { Modal } from './Modal';

/**
 * Import a Markdown/text document from a public URL. Fetching goes through the
 * same-origin API proxy (no CORS); failures surface a clean, specific message.
 */
export function ImportUrlDialog() {
  const t = useT();
  const open = useStore((s) => s.importUrlOpen);
  const setOpen = useStore((s) => s.setImportUrlOpen);
  const controller = useDocControllerContext();
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const close = () => {
    setOpen(false);
    setUrl('');
    setError(null);
    setBusy(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = url.trim();
    if (!value || busy) return;
    setBusy(true);
    setError(null);
    try {
      const id = await importFromUrl(value);
      await controller.open(id);
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('importUrl.failed'));
      setBusy(false);
    }
  };

  return (
    <Modal onClose={close} title={t('importUrl.title')}>
      <p className="mb-modal-note">{t('importUrl.hint')}</p>
      <form onSubmit={(e) => void submit(e)}>
        <input
          className="mb-modal-url"
          type="url"
          inputMode="url"
          autoFocus
          placeholder="https://example.com/document.md"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          aria-label={t('importUrl.title')}
          aria-invalid={!!error}
        />
        {error && (
          <p className="mb-modal-note mb-import-error" role="alert">
            {error}
          </p>
        )}
        <div className="mb-modal-actions">
          <button type="button" className="mb-btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="mb-btn mb-btn-primary" disabled={busy || !url.trim()}>
            {busy ? t('importUrl.importing') : t('importUrl.import')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
